import {
  Dispatch,
  forwardRef,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import Markdown from 'react-markdown';
import { parseDate } from '@Generator/history/GenColumns';
import { UserPromptVersion, UserSavedPrompt } from '@Models/types';
import {
  ArrowDown,
  ArrowsOutCardinal,
  PencilSimple,
  Robot,
  PaperclipHorizontal,
} from '@phosphor-icons/react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { GPT_WSS } from '@Env/variables';
import { toGPTMessage } from '@GPT/views/chat/websockets';
import { BounceLoader, MoonLoader } from 'react-spinners';
import colors from '@Common/assets/colors';
import { useUtilityContext } from '@Context/prompt-toolbox/UtilityContext';
import { Tooltip } from 'react-tooltip';
import { useAuth } from '@Context/CognitoAuthProvider';
import showToast from '@Common/toast/showToast';
import FileAttachmentDisplay from '../../../common/FileAttachmentDisplay';
import {
  createMessageWithAttachments,
  showFileProcessingToast,
  validatePromptBeforeSend,
  handleFileSelectionUpload,
  removeAttachmentAtIndex,
  clearAllAttachments,
  UploadResult,
  getFileTypeInfo,
} from '@Utils/FileAttachmentUtils';

interface Props {
  setDisable: Dispatch<SetStateAction<boolean>>;
}

export interface GPTMessage {
  sender: 'you' | 'gpt';
  message: string;
  dateSent: Date;
}

const MAX_RECONNECT = 3;

const Add = forwardRef<
  { getValues: () => Partial<UserSavedPrompt>; clearValues: () => void },
  Props
>(({ setDisable }, ref) => {
  const [promptName, setPromptName] = useState('');
  const [promptContent, setPromptContent] = useState('');
  const [message, setMessage] = useState<GPTMessage | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [WssAuthError, setWssAuthError] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const { setModal } = useUtilityContext();

  const { user } = useAuth();
  const { logout } = useAuth();

  const [attachments, setAttachments] = useState<UploadResult[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelection = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files || []);
    await handleFileSelectionUpload(
      files,
      logout,
      setProcessing,
      setAttachments,
      () => {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    );
  };

  const removeAttachment = (index: number) => {
    removeAttachmentAtIndex(index, setAttachments);
  };
  const { refreshIfExpired } = useAuth();
  const reconnectOnAbnormalClose = useRef(0);

  const { sendMessage, lastMessage, readyState } = useWebSocket(GPT_WSS, {
    onOpen: async () => {
      try {
        await refreshIfExpired(localStorage.getItem('accessToken'));
      } catch {
        logout();
      }
      console.log('[ socket: OPENED ]');
      sendMessage(
        JSON.stringify({
          action: 'sendmsgtest',
          token: localStorage.getItem('accessToken'),
        })
      );
    },
    onClose: (event) => {
      console.log('[ socket: CLOSED ]', event.code, event.reason);
      if (event.code === 1006) {
        reconnectOnAbnormalClose.current += 1;
      }
      if (
        event.code === 1008 ||
        reconnectOnAbnormalClose.current >= MAX_RECONNECT ||
        WssAuthError
      ) {
        showToast.error(
          'Session invalid. Please log in again to continue using the app.',
          'wss-auth-error'
        );

        logout();
      }
    },

    shouldReconnect: () =>
      true && reconnectOnAbnormalClose.current < MAX_RECONNECT,
    reconnectAttempts: MAX_RECONNECT,
  });

  const { loaders, setLoader } = useUtilityContext();
  const [processing, setProcessing] = useState(false);
  const [lastProcessedChunk, setLastProcessedChunk] = useState<string>('');
  const [processedFileContent, setProcessedFileContent] = useState<string>('');

  // Use refs to store setLoader to avoid dependency issues
  const setLoaderRef = useRef(setLoader);
  setLoaderRef.current = setLoader;

  // Stable function that won't change on every render
  const updateGptAddLoader = useCallback((loading: boolean) => {
    setLoaderRef.current('gptAdd', loading);
  }, []);

  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      setIsConnected(true);
      updateGptAddLoader(false);
      setIsReconnecting(false);
    } else if (readyState === ReadyState.CLOSED && isConnected) {
      console.log('Socket Disconnected');
      setIsReconnecting(true);
    } else {
      setIsReconnecting(false);
    }
  }, [readyState, isConnected, updateGptAddLoader]);

  useEffect(() => {
    if (lastMessage !== null) {
      try {
        const dataObj = JSON.parse(lastMessage.data);
        const { action, type, message: streamMessage } = dataObj;
        const messageObj = JSON.parse(streamMessage);

        if (action === 'chat') {
          const dateSent = messageObj.dateTime
            ? new Date(messageObj.dateTime)
            : new Date();

          const responseMessage: GPTMessage = {
            sender: 'gpt',
            message: messageObj.response,
            dateSent: dateSent,
          };

          setMessage(responseMessage);
          updateGptAddLoader(false);
        } else if (action === 'processing') {
          updateGptAddLoader(messageObj.loading);
        } else if (action === 'error') {
          if (type === 'auth-error') {
            setWssAuthError(true);
          } else if (type !== 'auth-timeout') {
            const responseMessage: GPTMessage = {
              sender: 'gpt',
              message: `ERROR‼️ ${messageObj.details}\n Please contact your system administrator.`,
              dateSent: new Date(),
            };
            setProcessing(false);
            updateGptAddLoader(false);
            setMessage(responseMessage);
          }
        } else if (action === 'stream') {
          const chunkContent = messageObj.response;

          // Prevent duplicate chunk processing
          if (chunkContent === lastProcessedChunk) {
            return;
          }
          setLastProcessedChunk(chunkContent);

          setProcessing(false);
          setMessage((currentMessage) => {
            if (!currentMessage) {
              const dateSent = messageObj.dateTime
                ? new Date(messageObj.dateTime)
                : new Date();

              return {
                sender: 'gpt',
                message: chunkContent,
                dateSent: dateSent,
              };
            } else {
              return {
                ...currentMessage,
                message: currentMessage.message + chunkContent,
              };
            }
          });
        } else if (action === 'stream_end') {
          // Handle processed file content when streaming ends
          if (messageObj.processedFileContent) {
            setProcessedFileContent(messageObj.processedFileContent);
          }

          setProcessing(false);
          updateGptAddLoader(false);
        }
      } catch (err) {
        console.error('Error parsing message data:', err);
      }
    }
  }, [lastMessage, lastProcessedChunk, updateGptAddLoader]);

  const handleSend = async () => {
    if (!validatePromptBeforeSend(promptContent, attachments)) {
      return;
    }

    try {
      let messageToSend = promptContent;

      if (attachments.length > 0) {
        attachments.forEach((attachment, index) => {
          const fileTypeInfo = getFileTypeInfo(attachment.fileName);
          console.log(`File ${index + 1}:`, {
            fileName: attachment.fileName,
            key: attachment.key,
            detectedType: fileTypeInfo.type,
            detectedIcon: fileTypeInfo.icon,
            fileExtension: attachment.fileName.split('.').pop()?.toLowerCase(),
          });
        });
        const fileTypes = attachments.map(
          (att) => getFileTypeInfo(att.fileName).type
        );

        messageToSend = createMessageWithAttachments(
          promptContent,
          attachments
        );
        const hasPdf = fileTypes.includes('pdf');
        const hasImage = fileTypes.includes('image');

        if (hasPdf && hasImage) {
          showToast.info(
            'Processing PDF and image files. PDF text extraction may take a moment...'
          );
        } else if (hasPdf) {
          showToast.info(
            'Processing PDF file. Text extraction may take a moment...'
          );
        } else {
          showFileProcessingToast(attachments.length);
        }
      }

      updateGptAddLoader(true);
      setProcessing(true);
      setMessage(null);
      setLastProcessedChunk('');
      sendMessage(toGPTMessage(messageToSend, null, 'onetime'));
    } catch (error) {
      console.error('Error sending message:', error);
      showToast.error('Failed to send message');
      updateGptAddLoader(false);
      setProcessing(false);
    }
  };

  const author = user?.profile?.username;
  const timestamp = Date.now();
  const date = parseDate(timestamp);

  const vers: UserPromptVersion = useMemo(
    () => ({
      gpt_result: message ? message.message : '',
      updatedBy: String(user?.profile?.name),
      prompt: promptContent,
      timestamp: String(timestamp),
      version: '1.0',
    }),
    [message, user?.profile?.name, promptContent, timestamp]
  );

  const toAdd: Omit<UserSavedPrompt, 'id'> = {
    name: promptName,
    username: author!,
    author: String(user?.profile?.name),
    version: [vers],
    processedFilePrompt: processedFileContent || undefined,
  };

  useImperativeHandle(ref, () => ({
    getValues: () => toAdd,
    clearValues: () => {
      setPromptName('');
      setPromptContent('');
      clearAllAttachments(setAttachments);
      setMessage(null);
      setProcessedFileContent('');
    },
  }));

  useEffect(() => {
    if (vers.gpt_result !== '') {
      setDisable(false);
    } else {
      setDisable(true);
    }
  }, [vers, setDisable]);

  return (
    <div className="relative flex h-full w-full flex-col items-start gap-y-2">
      {isReconnecting && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-y-3 rounded-xl bg-black opacity-90">
          <div className="flex flex-col items-center gap-y-2 rounded-md p-4">
            <MoonLoader color="white" />
            <label className="text-4xl font-bold text-white">
              Chat Session Ended
            </label>
            <label className="animate-pulse text-xl font-extrabold text-primary-500">
              Attempting to reconnect
            </label>
          </div>
        </div>
      )}
      <div className="flex w-full flex-col md:flex-row md:gap-x-2">
        <div className="w-full rounded-lg border-2 border-gray-200 p-3 md:w-3/5">
          <div className="flex flex-col gap-y-2">
            <div className="flex flex-row items-center gap-x-2">
              <PencilSimple size={15} weight="bold" />
              <div className="text-xs font-bold">Name</div>
            </div>
            <div className="w-full">
              <input
                value={promptName}
                onChange={(e) => setPromptName(e.target.value)}
                maxLength={150}
                type="text"
                placeholder="Enter prompt name"
                className="w-full border-2 border-gray-400 p-2 focus:outline-none"
              />
            </div>
          </div>
        </div>
        <div className="flex w-full flex-row gap-x-2 rounded-lg border-2 border-gray-200 p-3 md:w-2/5">
          <div className="flex w-full flex-col gap-y-2 md:w-1/2">
            <div className="text-xs font-bold">Author</div>
            <div className="overflow-y-auto rounded-md border-2 border-gray-300 p-2">
              <label className="">{user?.profile?.name}</label>
            </div>
          </div>
          <div className="flex w-full flex-col gap-y-2 md:w-1/2">
            <div className="text-xs font-bold">Date</div>
            <div className="overflow-y-auto rounded-md border-2 border-gray-300 p-2">
              <label className="">{date}</label>
            </div>
          </div>
        </div>
      </div>

      <div className="h-full w-full rounded-lg border-2 border-gray-200 p-3">
        <div className="flex h-full flex-col gap-y-2">
          <div className="flex flex-row items-center gap-x-2">
            <PencilSimple size={15} weight="bold" />
            <div className="text-xs font-bold">Prompt</div>
          </div>

          <textarea
            spellCheck={false}
            value={promptContent}
            onChange={(e) => setPromptContent(e.target.value)}
            maxLength={25000}
            placeholder="Enter prompt content"
            data-testid="new-prompt-content-input"
            className="custom-scroll h-full w-full resize-none overflow-y-auto border-2 border-gray-400 p-2 focus:outline-none"
          />
          <div className="flex w-full flex-row items-end gap-x-2">
            <div className="ml-2 flex flex-row items-center text-sm text-gray-600">
              <FileAttachmentDisplay
                attachments={attachments}
                onRemoveAttachment={removeAttachment}
              />
            </div>

            <div className="flex flex-1 flex-row items-center justify-end gap-x-2">
              {/* Only show buttons if not uploading */}
              {!isConnected || loaders.gptAdd ? (
                <div className="flex flex-row items-center gap-x-2 rounded-full bg-primary-700 px-4 py-2 text-white">
                  <BounceLoader color={colors.brandWarning[300]} size={15} />
                  {!isConnected && 'Connecting'}
                  {loaders.gptAdd && 'Generating'}
                </div>
              ) : (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelection}
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.gif,.bmp,.tiff,.webp,.md,.json,.txt,.csv,.xml,.yaml,.yml"
                    style={{ display: 'none' }}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={processing}
                  >
                    <PaperclipHorizontal
                      size={20}
                      className={`mr-5 rotate-90 ${
                        processing
                          ? 'text-gray-300'
                          : 'text-primary-700 hover:text-primary-500'
                      }`}
                    />
                  </button>
                  <button
                    onClick={handleSend}
                    data-testid="new-prompt-send-button"
                    disabled={processing}
                    className="flex flex-row items-center gap-x-2 rounded-full bg-primary-700 px-4 py-2 hover:shadow-md disabled:bg-gray-200 disabled:text-gray-400"
                  >
                    {processing ? (
                      <>
                        <BounceLoader
                          size={20}
                          color={colors.brandWarning[500]}
                        />
                        <span>'Processing...'</span>
                      </>
                    ) : (
                      <>
                        <ArrowDown
                          size={15}
                          weight="bold"
                          className="text-white"
                        />
                        <label className="text-white">Send</label>
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="h-full w-full rounded-lg border-2 border-gray-200 p-3">
        <div className="flex h-full flex-col gap-y-2">
          <div className="flex flex-row items-center gap-x-2">
            <Robot size={15} weight="bold" />
            <div className="text-xs font-bold">AI Response</div>
          </div>
          <div className="group relative flex h-full w-full flex-col border-2 border-gray-300">
            {!message && !loaders.gptAdd && !processing && (
              <span className="flex h-full flex-col items-center justify-center text-neutral-300">
                <Robot size={150} />
                <label className="text-xs font-medium tracking-wider text-neutral-500">
                  No AI Response
                </label>
              </span>
            )}
            {processing && (
              <span className="flex h-full flex-col items-center justify-center gap-y-3 text-neutral-300">
                <MoonLoader size={100} />
                <label className="animate-pulse text-xs font-medium tracking-wider text-neutral-500">
                  Generating Response
                </label>
              </span>
            )}

            {message && message.message && (
              <div
                data-testid="new-prompt-response"
                className="h-0 w-full flex-grow pr-1"
              >
                <Markdown className="markdown animate-typing custom-scroll h-full w-full flex-grow resize-none overflow-y-auto p-2 text-sm focus:outline-none">
                  {message.message}
                </Markdown>
              </div>
            )}
            {message && message.message && !loaders.gptAdd && (
              <div className="absolute bottom-2 right-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <button
                  data-tooltip-id="expand-add"
                  data-tooltip-content="Expand"
                  data-tooltip-place="left"
                  className="translate-y-4 transform rounded-full bg-primary-700 p-2 transition-transform duration-300 hover:shadow-xl group-hover:translate-y-0"
                  onClick={() => setModal('expandAddNewPromptGPTResult', true)}
                >
                  <ArrowsOutCardinal
                    size={20}
                    weight="bold"
                    className="text-white"
                  />
                </button>
                <Tooltip
                  id="expand-add"
                  style={{
                    color: 'white',
                    background: colors.primary[700],
                    borderRadius: '0.5rem',
                  }}
                  className="font-medium"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default Add;
